import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UploadApiResponse as CloudinaryResponse } from 'cloudinary';
import { PopperTooltip, Notification } from '@components/shared';
import { Checkbox, Dialog, InfoIcon, InlineNotice } from '@viewthespace/components';
import { AdminBuilding, CloudinaryImageParams, CropperCanvasData } from '@root/types';
import { useMutation } from '@tanstack/react-query';
import routes from '@root/routes';
import { update } from '@root/shared/typedApi';
import useCloudinaryUploader from '@components/shared/Cloudinary/useCloudinaryUploader';
import useCropperModal, {
  CropperModalState,
  OnSaveArgs,
} from '@components/shared/Cropper/useCropperModal';
import EditableAsset from '@components/shared/EditableAsset/EditableAsset';
import AssetUploader from '@components/shared/EditableAsset/AssetUploader';

type LogoTypes = 'bannerImage' | 'lettermarkLogo';
type Props = {
  building: AdminBuilding;
  refetchBuilding: () => void;
};
type CropperModalCustomAttributes = {
  shouldUseLandlordImage: boolean;
};

const BrandedAssets = ({ building, refetchBuilding }: Props) => {
  const { t } = useTranslation('admin');
  const [uploadNewPhotoType, setUploadNewPhotoType] = useState<LogoTypes | null>(null);
  const { CropperModal, cropperModalProps, openCropperModal } =
    useCropperModal<CropperModalCustomAttributes>();

  const onReady = (assetType: LogoTypes | null) => async (response: CloudinaryResponse) => {
    const cloudinaryId = response.public_id;
    if (assetType === 'lettermarkLogo') {
      editLettermarkLogo({ cloudinaryId, isNewUpload: true });
    } else if (assetType === 'bannerImage') {
      editBannerImage({ cloudinaryId, isNewUpload: true });
    }
  };

  const createErrorNotification = (error: string = '') => {
    Notification.error({
      title: t('notification.updateError'),
      text: error,
    });
  };

  const {
    selectAndUploadToCloudinary,
    CloudinaryUploaderFileInput,
    cloudinaryUploaderFileInputProps,
  } = useCloudinaryUploader({
    uploadToVTS: false,
    onReady: onReady(uploadNewPhotoType),
    onError: createErrorNotification,
  });

  const [deleteConfig, setDeleteConfig] = useState<{
    isOpen: boolean;
    assetType: LogoTypes | null;
  }>({ isOpen: false, assetType: null });

  const updateBuildingMutation = useMutation(
    async (updateParams: {
      bannerImage?: CloudinaryImageParams;
      lettermarkLogo?: CloudinaryImageParams;
      useLandlordLettermarkLogo?: boolean;
      useLandlordBannerImage?: boolean;
    }) => {
      await update(routes.api.admin.building(building?.id), updateParams);
    },
    {
      onSuccess: async () => {
        refetchBuilding();
        Notification.info({
          title: t('common:changesSaved'),
          placement: 'topRight',
        });
      },
    },
  );

  const handleSelectUploadNewImage = (assetType: LogoTypes) => {
    setUploadNewPhotoType(assetType);
    if (selectAndUploadToCloudinary) selectAndUploadToCloudinary();
  };

  const onSaveImageEdit =
    (assetType: LogoTypes) =>
    ({
      cloudinaryId,
      cropperCanvasData,
      cloudinaryTransformations,
      customAttributes,
    }: OnSaveArgs<CropperModalCustomAttributes>) => {
      const imageSourceParams = {
        [assetType === 'bannerImage' ? 'useLandlordBannerImage' : 'useLandlordLettermarkLogo']:
          !!customAttributes?.shouldUseLandlordImage,
      };
      const params = customAttributes?.shouldUseLandlordImage
        ? imageSourceParams
        : {
            [assetType]: {
              cloudinaryId,
              cropperCanvasData,
              transformations: cloudinaryTransformations,
            },
            ...imageSourceParams,
          };
      updateBuildingMutation.mutate(params);
    };

  type EditImageArgs = {
    cloudinaryId: string;
    cropperCanvasData?: CropperCanvasData | null;
    isNewUpload?: boolean;
  };
  const editLettermarkLogo = ({
    cloudinaryId,
    cropperCanvasData,
    isNewUpload = false,
  }: EditImageArgs) => {
    type Args = { shouldUseLandlordImage: boolean };
    type Return = Pick<CropperModalState, 'customAttributes' | 'viewMode' | 'imageSrc'>;
    const getSourceRelatedAttributes = ({ shouldUseLandlordImage }: Args): Return => ({
      customAttributes: { shouldUseLandlordImage },
      viewMode: shouldUseLandlordImage ? 'simplePreview' : 'edit',
      imageSrc: shouldUseLandlordImage ? building.landlordLettermarkLogo?.largePath : null,
    });

    const shouldUseLandlordImage = !isNewUpload && building.isUsingLandlordLettermarkLogo;
    openCropperModal({
      cloudinaryId: shouldUseLandlordImage ? null : cloudinaryId,
      initialCanvasData: cropperCanvasData,
      title: t('building.brandedAssets.editModal.lettermarkLogo.title'),
      cropBoxDimentions: { height: 295, width: 295 },
      onSave: onSaveImageEdit('lettermarkLogo'),
      alt: t('building.brandedAssets.editModal.lettermarkLogo.alt'),
      ...getSourceRelatedAttributes({ shouldUseLandlordImage }),
      afterUpload: (_state, updateState) =>
        updateState(getSourceRelatedAttributes({ shouldUseLandlordImage: false })),
      renderAdditionalFooterContent: building.landlordLettermarkLogo
        ? ({ customAttributes }, updateState) => (
            <Checkbox
              isChecked={!!customAttributes?.shouldUseLandlordImage}
              label={t('building.brandedAssets.editModal.lettermarkLogo.useLandlordLogo')}
              onChange={e =>
                updateState(getSourceRelatedAttributes({ shouldUseLandlordImage: e.value }))
              }
            />
          )
        : null,
    });
  };

  const editBannerImage = ({
    cloudinaryId,
    cropperCanvasData,
    isNewUpload = false,
  }: EditImageArgs) => {
    type Args = { shouldUseLandlordImage: boolean };
    type Return = Pick<CropperModalState, 'customAttributes' | 'viewMode' | 'imageSrc'>;
    const getSourceRelatedAttributes = ({ shouldUseLandlordImage }: Args): Return => ({
      customAttributes: { shouldUseLandlordImage },
      viewMode: shouldUseLandlordImage ? 'simplePreview' : 'edit',
      imageSrc: shouldUseLandlordImage ? building.landlordBannerImage?.largePath : null,
    });

    const shouldUseLandlordImage = !isNewUpload && building.isUsingLandlordBannerImage;
    openCropperModal({
      cloudinaryId: shouldUseLandlordImage ? null : cloudinaryId,
      initialCanvasData: cropperCanvasData,
      title: t('building.brandedAssets.editModal.banner.title'),
      cropBoxDimentions: { height: 93, width: 649 },
      onSave: onSaveImageEdit('bannerImage'),
      alt: t('building.brandedAssets.editModal.banner.alt'),
      ...getSourceRelatedAttributes({ shouldUseLandlordImage }),
      afterUpload: (_state, updateState) =>
        updateState(getSourceRelatedAttributes({ shouldUseLandlordImage: false })),
      renderAdditionalFooterContent: building.landlordBannerImage
        ? ({ customAttributes }, updateState) => (
            <Checkbox
              isChecked={!!customAttributes?.shouldUseLandlordImage}
              label={t('building.brandedAssets.editModal.banner.useLandlordBanner')}
              onChange={e =>
                updateState(getSourceRelatedAttributes({ shouldUseLandlordImage: e.value }))
              }
            />
          )
        : null,
    });
  };

  const onDeleteConfirm = async () => {
    if (deleteConfig.assetType) {
      updateBuildingMutation.mutate({
        [deleteConfig.assetType]: {
          cloudinaryId: null,
        },
        [deleteConfig.assetType === 'bannerImage'
          ? 'useLandlordBannerImage'
          : 'useLandlordLettermarkLogo']: false,
      });
      setDeleteConfig({ isOpen: false, assetType: null });
    }
  };

  const getAlternativeAssetSelection = (assetType: LogoTypes) => {
    const handleClickAlternativeAsset = () => {
      const params =
        assetType === 'lettermarkLogo'
          ? { useLandlordLettermarkLogo: true }
          : { useLandlordBannerImage: true };
      updateBuildingMutation.mutate(params);
    };

    return assetType === 'lettermarkLogo' ? (
      <div
        onClick={() => handleClickAlternativeAsset()}
        role="button"
        className="flex h-[147px] w-[264px] items-center justify-center gap-1 border border-solid bg-background-primary"
      >
        {building.landlordLettermarkLogo && (
          <img
            className="h-5 w-5"
            src={building.landlordLettermarkLogo.path}
            alt="landlord lettermark logo"
          />
        )}
        <span>{t('building.brandedAssets.addModal.useLandlordLogo')}</span>
      </div>
    ) : (
      <div
        onClick={() => handleClickAlternativeAsset()}
        role="button"
        className="flex h-[147px] w-[264px] flex-col items-center justify-center gap-1 border border-solid bg-background-primary"
      >
        <span>{t('building.brandedAssets.addModal.useLandlordBanner')}</span>
        {building.landlordBannerImage && (
          <img className="w-30" src={building.landlordBannerImage.path} alt="landlord banner" />
        )}
      </div>
    );
  };

  return (
    <>
      {deleteConfig.isOpen && (
        <DeleteDialog
          isDeleteOpen={deleteConfig.isOpen}
          assetType={deleteConfig.assetType!}
          onClose={() => setDeleteConfig({ isOpen: false, assetType: null })}
          onConfirm={onDeleteConfirm}
        />
      )}
      <CloudinaryUploaderFileInput {...cloudinaryUploaderFileInputProps} />
      <div className="mt-3.5">
        <span className="font-title">{t('building.brandedAssets.logo')}</span>
      </div>
      <div className="mt-4 flex flex-col">
        <span className="font-subtitle">{t('building.brandedAssets.lettermarkLogo.title')}</span>
        <div className="flex flex-row">
          <span className="font-body-medium">
            {t('building.brandedAssets.lettermarkLogo.subtitle')}
          </span>
          <PopperTooltip
            showArrow
            trigger="click"
            toolTipPlacement="top"
            triggerElement={<InfoIcon className="mb-1 ml-1 text-indigo-100" size="sm" />}
            popperElement={
              <figure>
                <img
                  src="https://media.truva.com/assets/buildingpage-lettermark-logo-screenshot.png"
                  alt={t('building.brandedAssets.lettermarkLogo.previewAltText')}
                />
              </figure>
            }
          />
        </div>
        {building.isUsingLandlordLettermarkLogo && building.landlordLettermarkLogo ? (
          <div className="mb-[-8px] w-fit [&>*:first-child]:p-0.5 [&_div]:items-center [&_svg]:h-[14px] [&_svg]:min-h-[14px] [&_svg]:w-[14px] [&_svg]:min-w-[14px]">
            <InlineNotice
              content={
                <span className="font-body-small">
                  {t('building.brandedAssets.lettermarkLogo.inlineNotice')}
                </span>
              }
            />
          </div>
        ) : null}
        <div className="pt-2" data-testid="lettermark-logo-display">
          {building.lettermarkLogo?.cloudinaryId ? (
            <div className="flex flex-col">
              <EditableAsset
                imagePath={building.lettermarkLogo?.path}
                imageClassName="!h-[110px] w-[110px] rounded-[3px] !border !border-solid !border-black-020 object-cover"
                altText={t('building.brandedAssets.lettermarkLogo.assetAltText')}
                editButtonClassName="relative ml-1 top-[46px] border border-solid border-black-035"
                editButtonName="lettermark-logo-edit-button"
                onUpload={() => handleSelectUploadNewImage('lettermarkLogo')}
                onDelete={() => setDeleteConfig({ isOpen: true, assetType: 'lettermarkLogo' })}
                onEdit={() => editLettermarkLogo(building.lettermarkLogo!)}
                items={['upload', !building.isUsingLandlordLettermarkLogo && 'edit', 'delete']}
              />
              <span className="pt-1 text-black-055 font-body-small">
                {t('building.brandedAssets.lettermarkLogo.imageSize')}
              </span>
            </div>
          ) : (
            <AssetUploader
              placeholderContainerClassName="flex flex-col items-center pl-[10px] pr-1"
              placeholderElementClassName="text-center font-body-medium"
              containerClassName="w-[110px] !h-[110px] !my-0"
              recommendedImageText={t('building.brandedAssets.lettermarkLogo.imageSize')}
              onReady={onReady('lettermarkLogo')}
              onError={createErrorNotification}
              testId="lettermark-logo-uploader"
              assetSelectorModalProps={
                building.landlordLettermarkLogo
                  ? {
                      title: t('building.brandedAssets.addModal.addLettermarkLogo'),
                      alternativeAssetSelection: getAlternativeAssetSelection('lettermarkLogo'),
                    }
                  : undefined
              }
            />
          )}
        </div>
      </div>

      <div className="mt-6 h-[1px] w-full bg-black-010" />
      <div className="mt-6 flex flex-col">
        <span className="font-title">{t('building.brandedAssets.banner.title')}</span>
        <div className="mt-4 flex flex-row">
          <span className="font-body-medium">{t('building.brandedAssets.banner.subtitle')}</span>
          <PopperTooltip
            showArrow
            toolTipPlacement="top"
            trigger="click"
            triggerElement={<InfoIcon className="mb-1 ml-1 text-indigo-100" size="sm" />}
            popperElement={
              <figure>
                <img
                  src="https://media.truva.com/assets/buildingpage-banner-screenshot.png"
                  alt={t('building.brandedAssets.banner.previewAltText')}
                />
              </figure>
            }
          />
        </div>
        {building.isUsingLandlordBannerImage && building.landlordBannerImage ? (
          <div className="mb-[-8px] w-fit [&>*:first-child]:p-0.5 [&_div]:items-center [&_svg]:h-[14px] [&_svg]:min-h-[14px] [&_svg]:w-[14px] [&_svg]:min-w-[14px]">
            <InlineNotice
              content={
                <span className="font-body-small">
                  {t('building.brandedAssets.banner.inlineNotice')}
                </span>
              }
            />
          </div>
        ) : null}
        <div className="pb-3 pt-2" data-testid="banner-image-display">
          {building.bannerImage?.cloudinaryId ? (
            <>
              <EditableAsset
                containerClassName="h-[139px] relative max-w-[1440px] w-full"
                imagePath={building.bannerImage.path}
                imageClassName="!h-[139px] w-[1440px] rounded-[3px] !border !border-solid !border-black-020 object-cover"
                altText={t('building.brandedAssets.banner.assetAltText')}
                editButtonClassName="absolute bottom-1 right-1 border-black-035 border border-solid"
                editButtonName="banner-edit-button"
                onUpload={() => handleSelectUploadNewImage('bannerImage')}
                onDelete={() => setDeleteConfig({ isOpen: true, assetType: 'bannerImage' })}
                onEdit={() => editBannerImage(building.bannerImage!)}
                items={['upload', !building.isUsingLandlordBannerImage && 'edit', 'delete']}
              />
              <span className="flex pt-1 text-black-055 font-body-small">
                {t('building.brandedAssets.banner.imageSize')}
              </span>
            </>
          ) : (
            <AssetUploader
              placeholderElementClassName="pl-1 font-body-medium"
              onReady={onReady('bannerImage')}
              onError={createErrorNotification}
              containerClassName="!h-[139px] !my-0 w-full max-w-[1440px]"
              recommendedImageText={t('building.brandedAssets.banner.imageSize')}
              testId="banner-uploader"
              assetSelectorModalProps={
                building.landlordBannerImage
                  ? {
                      title: t('building.brandedAssets.addModal.addBanner'),
                      alternativeAssetSelection: getAlternativeAssetSelection('bannerImage'),
                    }
                  : undefined
              }
            />
          )}
        </div>
      </div>
      <CropperModal {...cropperModalProps} />
    </>
  );
};

const DeleteDialog = ({
  isDeleteOpen,
  assetType,
  onClose,
  onConfirm,
}: {
  isDeleteOpen: boolean;
  assetType: LogoTypes;
  onClose: () => void;
  onConfirm: () => void;
}) => {
  const { t } = useTranslation('admin');

  const assetConfig = {
    lettermarkLogo: {
      title: t('building.brandedAssets.deleteModal.lettermarkLogo.title'),
      text: t('building.brandedAssets.deleteModal.lettermarkLogo.text'),
    },
    bannerImage: {
      title: t('building.brandedAssets.deleteModal.banner.title'),
      text: t('building.brandedAssets.deleteModal.banner.text'),
    },
  };

  return (
    <Dialog
      align="default"
      header={assetConfig[assetType].title}
      isOpen={isDeleteOpen}
      onClose={onClose}
      closeOnClickAway
      width={assetType === 'bannerImage' ? 'auto' : 'small'}
      primaryButton={{
        text: t('common:confirm'),
        onClick: onConfirm,
      }}
      secondaryButton={{
        text: t('common:cancel'),
        onClick: onClose,
      }}
    >
      <div className="p-2">
        <span className="font-body-medium">{assetConfig[assetType].text}</span>
      </div>
    </Dialog>
  );
};

export default BrandedAssets;
